import { useApiClient } from '@klareospace/pharos-lib-front-utils';
import type { TemplateConfiguration } from '@klareospace/pharos-lib-types';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { createMutationCallbacks } from '@klareospace/pharos-lib-web-utils';

import { createTemplateConfigurationApi, templateConfigurationKeys } from '../api';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UpdateShipperVariables {
  id: string;
  values: Record<string, unknown>;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Mutation hook for editing a TemplateConfiguration via PATCH /template-configurations/:id.
 *
 * On success: invalidates both the list and the detail cache entry,
 * shows a success toast, and calls `onSuccess` (typically to close the drawer).
 * On error: shows an error toast.
 */
export function useUpdateShipper(
  onSuccess?: () => void,
): UseMutationResult<TemplateConfiguration, Error, UpdateShipperVariables> {
  const client = useApiClient('deliveries');
  const api = createTemplateConfigurationApi(client);
  const queryClient = useQueryClient();
  const { t } = useTranslation('settings');

  return useMutation<TemplateConfiguration, Error, UpdateShipperVariables>({
    mutationFn: ({ id, values }: UpdateShipperVariables): Promise<TemplateConfiguration> =>
      api.updateTemplateConfiguration(id, { values }),
    ...createMutationCallbacks<TemplateConfiguration, UpdateShipperVariables>({
      errorKey: 'shippers.edit.toast.error',
      invalidateKeys: ({ id }) => [templateConfigurationKeys.lists(), templateConfigurationKeys.detail(id)],
      onSuccess: onSuccess,
      queryClient,
      successKey: 'shippers.edit.toast.success',
      t,
    }),
  });
}
